import { Link } from 'wouter';
import { Zap, ArrowRight, ShieldCheck, Repeat, Gauge } from 'lucide-react';
import { Button } from '../components/ui';
import { useLocale } from '../contexts/LocaleContext';

function MarketingNav() {
  const { t } = useLocale();
  return (
    <header className="flex items-center justify-between px-6 py-4">
      <div className="flex items-center gap-2">
        <div className="grid h-8 w-8 place-items-center rounded-lg bg-primary text-primary-foreground">
          <Zap className="h-4 w-4" />
        </div>
        <span className="text-lg font-semibold">LeadHub</span>
      </div>
      <div className="flex items-center gap-3">
        <Link href="/login" className="text-sm text-muted-foreground hover:text-foreground">
          {t('static.signIn')}
        </Link>
        <Link href="/register">
          <Button size="sm">
            {t('static.getStarted')} <ArrowRight className="h-3.5 w-3.5" />
          </Button>
        </Link>
      </div>
    </header>
  );
}

export function LandingPage() {
  const { t } = useLocale();
  return (
    <div className="min-h-screen">
      <MarketingNav />
      <main className="mx-auto max-w-5xl px-6 py-20 text-center">
        <p className="text-sm font-semibold uppercase tracking-widest text-primary">
          {t('static.landingTagline')}
        </p>
        <h1 className="mt-4 text-4xl font-extrabold tracking-tight sm:text-5xl">
          {t('static.landingTitle')}
        </h1>
        <p className="mx-auto mt-5 max-w-2xl text-lg text-muted-foreground">{t('static.landingSubtitle')}</p>
        <div className="mt-8 flex items-center justify-center gap-3">
          <Link href="/register">
            <Button size="lg">
              {t('static.startFree')} <ArrowRight className="h-4 w-4" />
            </Button>
          </Link>
          <Link href="/login">
            <Button size="lg" variant="outline">
              {t('static.signIn')}
            </Button>
          </Link>
        </div>
        <div className="mt-20 grid gap-6 sm:grid-cols-3">
          {[
            { icon: <Gauge className="h-5 w-5" />, titleKey: 'static.feature1Title', bodyKey: 'static.feature1Body' },
            { icon: <Repeat className="h-5 w-5" />, titleKey: 'static.feature2Title', bodyKey: 'static.feature2Body' },
            { icon: <ShieldCheck className="h-5 w-5" />, titleKey: 'static.feature3Title', bodyKey: 'static.feature3Body' },
          ].map((f) => (
            <div key={f.titleKey} className="rounded-xl border bg-card p-6 text-left">
              <div className="grid h-9 w-9 place-items-center rounded-lg bg-primary/10 text-primary">
                {f.icon}
              </div>
              <h3 className="mt-3 font-semibold">{t(f.titleKey)}</h3>
              <p className="mt-1 text-sm text-muted-foreground">{t(f.bodyKey)}</p>
            </div>
          ))}
        </div>
      </main>
      <footer className="border-t px-6 py-8 text-center text-sm text-muted-foreground">
        <div className="flex justify-center gap-4">
          <Link href="/privacy" className="hover:text-foreground">
            {t('static.footerPrivacy')}
          </Link>
          <Link href="/terms" className="hover:text-foreground">
            {t('static.footerTerms')}
          </Link>
          <Link href="/data-deletion" className="hover:text-foreground">
            {t('static.footerDataDeletion')}
          </Link>
        </div>
        <p className="mt-3">{t('static.copyright', { year: new Date().getFullYear() })}</p>
      </footer>
    </div>
  );
}

function LegalShell({ title, children }: { title: string; children: React.ReactNode }) {
  const { t } = useLocale();
  return (
    <div className="min-h-screen">
      <MarketingNav />
      <main className="mx-auto max-w-3xl px-6 py-16">
        <h1 className="text-3xl font-bold">{title}</h1>
        <div className="prose prose-sm mt-6 max-w-none text-muted-foreground">{children}</div>
        <p className="mt-10">
          <Link href="/" className="text-primary hover:underline">
            {t('static.backHome')}
          </Link>
        </p>
      </main>
    </div>
  );
}

export const PrivacyPage = () => {
  const { t } = useLocale();
  return (
    <LegalShell title={t('static.privacyTitle')}>
      <p>{t('static.privacyP1')}</p>
      <p>{t('static.privacyP2')}</p>
    </LegalShell>
  );
};

export const TermsPage = () => {
  const { t } = useLocale();
  return (
    <LegalShell title={t('static.termsTitle')}>
      <p>{t('static.termsBody')}</p>
    </LegalShell>
  );
};

export const DataDeletionPage = () => {
  const { t } = useLocale();
  return (
    <LegalShell title={t('static.dataDeletionTitle')}>
      <p>{t('static.dataDeletionP1')}</p>
      <p>{t('static.dataDeletionP2')}</p>
    </LegalShell>
  );
};

export const NotFoundPage = () => {
  const { t } = useLocale();
  return (
    <div className="grid min-h-screen place-items-center bg-muted/30 p-6 text-center">
      <div>
        <p className="text-6xl font-extrabold text-primary">404</p>
        <p className="mt-2 text-lg font-medium">{t('static.notFoundTitle')}</p>
        <p className="mt-1 text-sm text-muted-foreground">{t('static.notFoundDescription')}</p>
        <Link href="/overview" className="mt-6 inline-block">
          <Button>{t('static.backDashboard')}</Button>
        </Link>
      </div>
    </div>
  );
};
